import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { getStoredUser } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Copy, Check, AlertTriangle, CheckCircle2, Clock, RefreshCw, FileText, X, ExternalLink, Send, CalendarDays } from "lucide-react";

/**
 * InboundSetupManager - Posting instructions for Lead and Appointment webhooks
 * 
 * Displays:
 * - Lead Posting Instructions (left column)
 * - Appointment Posting Instructions (right column)
 * - Posting Instructions modals for each
 * 
 * NOTE: Webhook Key lifecycle controls are in Admin → Calendars tab
 * NOTE: Lead Source API key lifecycle controls are in Admin → Lead Sources tab
 * 
 * P0 TENANT ISOLATION: Accepts tenantId prop for SuperAdmin cross-tenant access.
 */
export default function InboundSetupManager({ tenantId }) {
  const user = getStoredUser();
  const [leadSources, setLeadSources] = useState([]);
  const [webhookKey, setWebhookKey] = useState(null);
  const [calendarRoutes, setCalendarRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastLeadIntake, setLastLeadIntake] = useState(null);
  const [lastAppointment, setLastAppointment] = useState(null);
  const [lastLeadError, setLastLeadError] = useState(null);
  const [lastGhlError, setLastGhlError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  
  // Modal states
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);

  // Get base URL for posting instructions
  const baseUrl = window.location.origin || window.location.origin;

  // P0 TENANT ISOLATION: Build params with tenant_id for SuperAdmin cross-tenant access
  const buildTenantParams = useCallback(() => {
    return tenantId ? { tenant_id: tenantId } : {};
  }, [tenantId]);

  const loadData = useCallback(async () => {
    setLoading(true);
    const params = buildTenantParams();
    try {
      // Load lead sources
      const sourcesRes = await api.get("/admin/lead-sources", { params });
      setLeadSources(sourcesRes.data?.filter(s => s.status === "active") || []);

      // Load webhook key for GHL appointments
      try {
        const keyRes = await api.get("/webhooks/keys/gohighlevel_appointment", { params });
        setWebhookKey(keyRes.data);
      } catch {
        setWebhookKey(null);
      }

      // Load calendar routes
      try {
        const routesRes = await api.get("/admin/calendar-routes", { params });
        setCalendarRoutes(routesRes.data?.routes || routesRes.data || []);
      } catch {
        setCalendarRoutes([]);
      }

      // Load last lead intake event
      try {
        const eventsRes = await api.get("/lead-intake/events", { params: { limit: 1 } });
        if (eventsRes.data?.length > 0) {
          setLastLeadIntake(eventsRes.data[0]);
        }
      } catch {
        // Optional - don't fail if events endpoint unavailable
      }

      // Load last appointment
      try {
        const apptRes = await api.get("/appointments", { params: { ...params, limit: 1 } });
        if (apptRes.data?.length > 0) {
          setLastAppointment(apptRes.data[0]);
        }
      } catch {
        // Optional
      }

      // P0: Load last errors from audit log
      try {
        const auditRes = await api.get("/admin/audit/inbound-errors", { params });
        if (auditRes.data) {
          setLastLeadError(auditRes.data.lead_intake_error);
          setLastGhlError(auditRes.data.ghl_appointment_error);
        }
      } catch {
        // Audit endpoint may not exist yet
      }
    } catch (e) {
      toast({
        title: "Failed to load inbound setup data",
        description: e?.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [buildTenantParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const copyToClipboard = async (text, fieldId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldId);
      setTimeout(() => setCopiedField(null), 2000);
      toast({ title: "Copied!", description: "Copied to clipboard" });
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "Never";
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return ts;
    }
  };

  const CopyButton = ({ text, fieldId, size = "sm", className = "" }) => (
    <Button
      variant="ghost"
      size={size}
      className={`h-6 w-6 p-0 text-slate-400 hover:text-slate-100 hover:bg-slate-700/50 ${className}`}
      onClick={() => copyToClipboard(text, fieldId)}
      data-testid={`copy-btn-${fieldId}`}
      title="Copy to clipboard"
    >
      {copiedField === fieldId ? (
        <Check className="h-3 w-3 text-green-400" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
    </Button>
  );

  const sampleLeadPayload = JSON.stringify({
    first_name: "John",
    last_name: "Doe",
    phone: "+15551234567",
    email: "john@example.com",
    attribution: {
      utm_source: "meta",
      utm_campaign: "jan_campaign"
    }
  }, null, 2);

  const sampleAppointmentPayload = JSON.stringify({
    type: "AppointmentCreate",
    locationId: "abc123",
    contactId: "contact_456",
    calendarId: "calendar_789",
    startTime: "2024-01-15T10:00:00Z",
    endTime: "2024-01-15T11:00:00Z",
    title: "Consultation Call",
    appointmentStatus: "confirmed"
  }, null, 2);

  // Build full posting instructions for modal
  const getLeadPostingInstructions = () => {
    return `POST ${baseUrl}/api/leads/intake

Headers:
Content-Type: application/json
Authorization: Bearer <YOUR_API_KEY>

Sample Payload:
${sampleLeadPayload}`;
  };

  const getAppointmentPostingInstructions = () => {
    // P0 SECURITY: Never include full key in copy-all. User must rotate to get new key.
    return `POST ${baseUrl}/api/webhooks/gohighlevel/appointments

Headers:
Content-Type: application/json
Authorization: Bearer <YOUR_WEBHOOK_KEY>

Sample Payload:
${sampleAppointmentPayload}

Note: Full webhook key is shown once at creation/rotation. Rotate in Admin → Calendars if lost.`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-6 w-6 animate-spin text-indigo-400" />
        <span className="ml-2 text-slate-400">Loading inbound setup...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inbound-setup-manager">
      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* LEFT COLUMN: Lead Posting Instructions */}
        <Card className="glass border-white/10">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl text-white flex items-center gap-2">
                  <Send className="h-5 w-5 text-indigo-400" />
                  Lead Posting Instructions
                </CardTitle>
                <CardDescription className="text-slate-400 mt-1">
                  Send leads from forms, Meta, Google, or any external source
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLeadModalOpen(true)}
                className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-slate-100"
                data-testid="lead-posting-instructions-btn"
              >
                <FileText className="h-4 w-4 mr-1.5" />
                Posting Instructions
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Posting URL */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">Endpoint URL</label>
              <div className="flex items-center gap-2 bg-slate-800/50 rounded-lg p-2.5 font-mono text-sm">
                <code className="flex-1 text-emerald-300 break-all text-xs">
                  {baseUrl}/api/leads/intake
                </code>
                <CopyButton text={`${baseUrl}/api/leads/intake`} fieldId="lead-url" />
              </div>
            </div>

            {/* Required Headers - Individual copy buttons for each element */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">Required Headers</label>
              <div className="bg-slate-800/50 rounded-lg p-2.5 space-y-2">
                {/* Content-Type header */}
                <div className="flex items-center gap-1 text-xs font-mono">
                  <span className="text-slate-400">Content-Type</span>
                  <CopyButton text="Content-Type" fieldId="lead-ct-label" />
                  <span className="text-slate-500 mx-1">:</span>
                  <span className="text-slate-200">application/json</span>
                  <CopyButton text="application/json" fieldId="lead-ct-value" />
                </div>
                {/* Authorization header - no copy button, key comes from Lead Sources */}
                <div className="flex items-center gap-1 text-xs font-mono">
                  <span className="text-slate-400">Authorization</span>
                  <CopyButton text="Authorization" fieldId="lead-auth-label" />
                  <span className="text-slate-500 mx-1">:</span>
                  <span className="text-slate-200">Bearer </span>
                  <span className="text-amber-300/70">&lt;api_key&gt;</span>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Get your API key from Lead Sources. If lost, rotate in Lead Sources tab.
              </p>
            </div>

            {/* Active Lead Sources - Expanded scroll area */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                  Active Lead Sources ({leadSources.length})
                </label>
                {leadSources.length > 4 && (
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                    Scroll for more
                  </span>
                )}
              </div>
              {leadSources.length === 0 ? (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-amber-200 text-sm">
                  <AlertTriangle className="h-4 w-4 inline mr-2" />
                  No active lead sources. Create one in the Lead Sources tab.
                </div>
              ) : (
                <div className="relative">
                  <div className="space-y-2 max-h-80 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-slate-800/50 border border-slate-700/50 rounded-lg p-2 bg-slate-900/30">
                    {leadSources.map((source) => (
                      <div
                        key={source.id}
                        className="bg-slate-800/50 rounded-lg p-2.5 space-y-1.5 border border-slate-700/30"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-slate-100 font-medium text-sm">{source.name}</span>
                            <span className="text-slate-500 text-xs">→ {source.channel}</span>
                          </div>
                          {source.workspace_name && (
                            <Badge className="bg-indigo-500/20 text-indigo-200 text-xs py-0">
                              {source.workspace_name}
                            </Badge>
                          )}
                        </div>
                        
                        {/* API Key Row - Fingerprint only, no copy (key is non-retrievable) */}
                        {source.has_api_key ? (
                          <div className="flex items-center gap-2 bg-slate-900/50 rounded p-1.5">
                            <span className="text-slate-500 text-xs">API Key:</span>
                            <code className="flex-1 text-amber-300 font-mono text-xs">
                              {source.api_key_fingerprint}••••••••
                            </code>
                            <Badge className="text-xs bg-emerald-500/20 text-emerald-300 border-emerald-500/30 py-0">
                              Active
                            </Badge>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 bg-slate-900/50 rounded p-1.5 text-xs text-slate-500">
                            <AlertTriangle className="h-3 w-3 text-amber-400" />
                            No API key - generate one in Lead Sources tab
                          </div>
                        )}
                        
                        {/* Link to Lead Sources for key management */}
                        {source.has_api_key && (
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <ExternalLink className="h-3 w-3" />
                            Full key shown once at generation. Rotate in Lead Sources if lost.
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                  {leadSources.length > 4 && (
                    <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-slate-900/80 to-transparent pointer-events-none rounded-b-lg" />
                  )}
                </div>
              )}
            </div>

            {/* Status */}
            <div className="pt-2 border-t border-white/10 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs">
                  <Clock className="h-3.5 w-3.5 text-slate-500" />
                  <span className="text-slate-400">Last Received:</span>
                  <span className="text-slate-200">{formatTimestamp(lastLeadIntake?.created_at)}</span>
                </div>
                {lastLeadIntake && (
                  <Badge className={`text-xs ${lastLeadIntake.status === "accepted" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>
                    {lastLeadIntake.status || "unknown"}
                  </Badge>
                )}
              </div>
              
              {lastLeadError && (
                <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg p-2 text-xs">
                  <AlertTriangle className="h-3.5 w-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-red-300 font-medium">Last Error</span>
                      <span className="text-red-400/70">{formatTimestamp(lastLeadError.timestamp)}</span>
                    </div>
                    <p className="text-red-200/80 mt-0.5">
                      {lastLeadError.status_code && <span className="font-mono">[{lastLeadError.status_code}]</span>} {lastLeadError.message}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* RIGHT COLUMN: Appointment Posting Instructions */}
        <Card className="glass border-white/10">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl text-white flex items-center gap-2">
                  <CalendarDays className="h-5 w-5 text-indigo-400" />
                  Appointment Posting Instructions
                </CardTitle>
                <CardDescription className="text-slate-400 mt-1">
                  Paste this URL into GoHighLevel calendar webhook settings
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAppointmentModalOpen(true)}
                className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-slate-100"
                data-testid="appointment-posting-instructions-btn"
              >
                <FileText className="h-4 w-4 mr-1.5" />
                Posting Instructions
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Posting URL */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">Webhook URL</label>
              <div className="flex items-center gap-2 bg-slate-800/50 rounded-lg p-2.5 font-mono text-sm">
                <code className="flex-1 text-blue-300 break-all text-xs">
                  {baseUrl}/api/webhooks/gohighlevel/appointments
                </code>
                <CopyButton text={`${baseUrl}/api/webhooks/gohighlevel/appointments`} fieldId="ghl-url" />
              </div>
            </div>

            {/* Required Headers - Individual copy buttons for each element */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">Required Headers</label>
              <div className="bg-slate-800/50 rounded-lg p-2.5 space-y-2">
                {/* Content-Type header */}
                <div className="flex items-center gap-1 text-xs font-mono">
                  <span className="text-slate-400">Content-Type</span>
                  <CopyButton text="Content-Type" fieldId="ghl-ct-label" />
                  <span className="text-slate-500 mx-1">:</span>
                  <span className="text-slate-200">application/json</span>
                  <CopyButton text="application/json" fieldId="ghl-ct-value" />
                </div>
                {/* Authorization header - placeholder format, consistent with Lead card */}
                <div className="flex items-center gap-1 text-xs font-mono">
                  <span className="text-slate-400">Authorization</span>
                  <CopyButton text="Authorization" fieldId="ghl-auth-label" />
                  <span className="text-slate-500 mx-1">:</span>
                  <span className="text-slate-200">Bearer </span>
                  <span className="text-amber-300/70">&lt;api_key&gt;</span>
                </div>
              </div>
              {webhookKey ? (
                <p className="text-xs text-slate-500">
                  Full key shown once at creation/rotation. Rotate in Admin → Calendars if lost.
                </p>
              ) : (
                <p className="text-xs text-amber-200">
                  <AlertTriangle className="h-3 w-3 inline mr-1" />
                  No webhook key. Create a calendar in the Calendars tab.
                </p>
              )}
            </div>

            {/* Calendar Routes Summary */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                Calendar Routes ({calendarRoutes.filter(r => r.status === "active").length} active)
              </label>
              {calendarRoutes.length === 0 ? (
                <div className="bg-slate-800/50 rounded-lg p-3 text-slate-400 text-xs">
                  No calendar routes configured. Add calendars in the Calendars tab.
                </div>
              ) : (
                <div className="bg-slate-800/50 rounded-lg p-2.5 space-y-1.5 max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-slate-800/50">
                  {calendarRoutes.filter(r => r.status === "active").map((route) => (
                    <div key={route.id} className="flex items-center gap-2 text-xs">
                      <CheckCircle2 className="h-3 w-3 text-emerald-400 shrink-0" />
                      <code className="text-slate-400 truncate max-w-[120px]" title={route.calendar_id}>
                        {route.calendar_id}
                      </code>
                      <span className="text-slate-500">→</span>
                      <span className="text-slate-300 truncate">{route.lead_source_name || route.lead_source_id}</span>
                      {route.workspace_name && (
                        <Badge className="bg-indigo-500/20 text-indigo-200 text-xs py-0 shrink-0">
                          {route.workspace_name}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Status */}
            <div className="pt-2 border-t border-white/10 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs">
                  <Clock className="h-3.5 w-3.5 text-slate-500" />
                  <span className="text-slate-400">Last Received:</span>
                  <span className="text-slate-200">{formatTimestamp(lastAppointment?.created_at)}</span>
                </div>
                {lastAppointment && (
                  <Badge className="text-xs bg-blue-500/20 text-blue-300">
                    {lastAppointment.status || "received"}
                  </Badge>
                )}
              </div>
              
              {lastGhlError && (
                <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg p-2 text-xs">
                  <AlertTriangle className="h-3.5 w-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-red-300 font-medium">Last Error</span>
                      <span className="text-red-400/70">{formatTimestamp(lastGhlError.timestamp)}</span>
                    </div>
                    <p className="text-red-200/80 mt-0.5">
                      {lastGhlError.status_code && <span className="font-mono">[{lastGhlError.status_code}]</span>} {lastGhlError.message}
                      {lastGhlError.calendar_id && <span className="ml-1 text-red-400/60">(calendar: {lastGhlError.calendar_id})</span>}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lead Posting Instructions Modal */}
      <Dialog open={leadModalOpen} onOpenChange={setLeadModalOpen}>
        <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2">
              <Badge className="bg-emerald-500/20 text-emerald-300">POST</Badge>
              Lead Posting Instructions
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Complete instructions for posting leads to MissionAssist
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* Endpoint URL */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Endpoint URL</label>
              <div className="flex items-center gap-2 bg-slate-800/50 rounded-lg p-3 font-mono text-sm">
                <code className="flex-1 text-emerald-300 break-all">
                  {baseUrl}/api/leads/intake
                </code>
                <CopyButton text={`${baseUrl}/api/leads/intake`} fieldId="modal-lead-url" />
              </div>
            </div>

            {/* Required Headers */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Required Headers</label>
              <div className="bg-slate-800/50 rounded-lg p-3 font-mono text-sm space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">Content-Type</span>
                  <CopyButton text="Content-Type" fieldId="modal-lead-ct-label" />
                  <span className="text-slate-500">:</span>
                  <span className="text-slate-200">application/json</span>
                  <CopyButton text="application/json" fieldId="modal-lead-ct-value" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">Authorization</span>
                  <CopyButton text="Authorization" fieldId="modal-lead-auth-label" />
                  <span className="text-slate-500">:</span>
                  <span className="text-slate-200">Bearer </span>
                  <span className="text-amber-300/70">&lt;api_key&gt;</span>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Full key shown once at generation/rotation. Rotate in Lead Sources if lost.
              </p>
            </div>

            {/* Sample Payload */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Sample Payload</label>
              <div className="relative">
                <pre className="bg-slate-800/50 rounded-lg p-3 font-mono text-xs text-slate-300 overflow-x-auto whitespace-pre">
                  {sampleLeadPayload}
                </pre>
                <div className="absolute top-2 right-2">
                  <CopyButton text={sampleLeadPayload} fieldId="modal-lead-payload" />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="border-t border-white/10 pt-4">
            <Button
              variant="outline"
              onClick={() => copyToClipboard(getLeadPostingInstructions(), "modal-lead-all")}
              className="border-slate-600 text-slate-200 hover:bg-slate-800"
              data-testid="copy-all-lead-instructions"
            >
              {copiedField === "modal-lead-all" ? (
                <Check className="h-4 w-4 mr-2 text-green-400" />
              ) : (
                <Copy className="h-4 w-4 mr-2" />
              )}
              Copy All
            </Button>
            <Button
              onClick={() => setLeadModalOpen(false)}
              className="bg-slate-700 text-slate-100 hover:bg-slate-600"
            >
              <X className="h-4 w-4 mr-2" />
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Appointment Posting Instructions Modal */}
      <Dialog open={appointmentModalOpen} onOpenChange={setAppointmentModalOpen}>
        <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2">
              <Badge className="bg-blue-500/20 text-blue-300">POST</Badge>
              Appointment Posting Instructions
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Complete instructions for posting GHL appointments to MissionAssist
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* Endpoint URL */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Webhook URL</label>
              <div className="flex items-center gap-2 bg-slate-800/50 rounded-lg p-3 font-mono text-sm">
                <code className="flex-1 text-blue-300 break-all">
                  {baseUrl}/api/webhooks/gohighlevel/appointments
                </code>
                <CopyButton text={`${baseUrl}/api/webhooks/gohighlevel/appointments`} fieldId="modal-ghl-url" />
              </div>
            </div>

            {/* Required Headers */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Required Headers</label>
              <div className="bg-slate-800/50 rounded-lg p-3 font-mono text-sm space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">Content-Type</span>
                  <CopyButton text="Content-Type" fieldId="modal-ghl-ct-label" />
                  <span className="text-slate-500">:</span>
                  <span className="text-slate-200">application/json</span>
                  <CopyButton text="application/json" fieldId="modal-ghl-ct-value" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">Authorization</span>
                  <CopyButton text="Authorization" fieldId="modal-ghl-auth-label" />
                  <span className="text-slate-500">:</span>
                  <span className="text-slate-200">Bearer </span>
                  <span className="text-amber-300/70">&lt;api_key&gt;</span>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Full key shown once at creation/rotation. Rotate in Admin → Calendars if lost.
              </p>
            </div>

            {/* Sample Payload */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Sample Payload</label>
              <div className="relative">
                <pre className="bg-slate-800/50 rounded-lg p-3 font-mono text-xs text-slate-300 overflow-x-auto whitespace-pre">
                  {sampleAppointmentPayload}
                </pre>
                <div className="absolute top-2 right-2">
                  <CopyButton text={sampleAppointmentPayload} fieldId="modal-ghl-payload" />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="border-t border-white/10 pt-4">
            <Button
              variant="outline"
              onClick={() => copyToClipboard(getAppointmentPostingInstructions(), "modal-ghl-all")}
              className="border-slate-600 text-slate-200 hover:bg-slate-800"
              data-testid="copy-all-appointment-instructions"
            >
              {copiedField === "modal-ghl-all" ? (
                <Check className="h-4 w-4 mr-2 text-green-400" />
              ) : (
                <Copy className="h-4 w-4 mr-2" />
              )}
              Copy All
            </Button>
            <Button
              onClick={() => setAppointmentModalOpen(false)}
              className="bg-slate-700 text-slate-100 hover:bg-slate-600"
            >
              <X className="h-4 w-4 mr-2" />
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
